/**
 * Deploy watcher — notices that production has been rebuilt while this tab is
 * still running the previous build, and lets the app roll over to the new
 * build at a moment the advisor won't notice.
 *
 * Why not just reload when something breaks: Vercel replaces every hashed
 * asset the instant a merge deploys, and the Hobby plan has no skew
 * protection, so an open tab that asks for an on-demand chunk after a deploy
 * gets a 404 (`staleDeploy.ts` catches that). This module gets ahead of it.
 *
 * How detection works — no build stamp, no version file: the served
 * `index.html` is fetched fresh (cache bypassed) and its `/assets/…` URLs are
 * compared with the ones this document loaded at boot. Any served URL this
 * tab doesn't know about means a newer build is live. The comparison is
 * one-directional on purpose: Vite appends `<link rel="modulepreload">` tags
 * to the running document at runtime, so "this document has extra URLs" is
 * normal and must never count as a new deploy.
 *
 * Rollover policy — a pending update is applied ONLY at a navigation the
 * advisor initiated (`rolloverIfUpdatePending()` is called from those safe
 * points), never spontaneously while they are looking at a screen. Anyone
 * parked on one screen gets the small corner pill instead.
 */

const ASSET_URL_RE = /(?:src|href)=["']([^"']*\/assets\/[^"']+)["']/g;
const BUILD_STAMP_RE = /<meta\s+name=["']ttg-build["']\s+content=["']([^"']+)["']/i;

// Deliberately unhurried. Detection only has to beat the advisor's next
// navigation, and a tab returning to the foreground checks anyway, so a tight
// poll bought nothing but background requests.
const DEFAULT_INTERVAL_MS = 15 * 60 * 1000;
const DEFAULT_MIN_GAP_MS = 60 * 1000;

/** Reduce an asset URL (absolute or root-relative) to its `/assets/…` path. */
function assetPath(url: string): string {
  const i = url.indexOf('/assets/');
  return i === -1 ? url : url.slice(i);
}

/** Every `/assets/…` URL referenced by `<script src>` / `<link href>` in an HTML string. */
export function extractAssetUrls(html: string): string[] {
  const out = new Set<string>();
  for (const m of html.matchAll(ASSET_URL_RE)) out.add(assetPath(m[1]));
  return [...out];
}

/**
 * When the served build was made, as ms since epoch, from the `ttg-build`
 * meta that `vite.config.ts` stamps into index.html. Null for a page without
 * one (a build older than the stamp, the dev server, an error page).
 */
export function extractBuildTime(html: string): number | null {
  const m = BUILD_STAMP_RE.exec(html);
  if (!m) return null;
  const t = Date.parse(m[1]);
  return Number.isFinite(t) ? t : null;
}

/** The `/assets/…` URLs the running document was built with. */
export function currentAssetUrls(doc: Document = document): string[] {
  const out = new Set<string>();
  doc.querySelectorAll<HTMLElement>('script[src], link[href]').forEach((el) => {
    const url = el.getAttribute('src') ?? el.getAttribute('href') ?? '';
    if (url.includes('/assets/')) out.add(assetPath(url));
  });
  return [...out];
}

/**
 * True when the served HTML references an asset this document doesn't know.
 * Empty inputs never count: an unparseable/error response, or a dev-server
 * document with no hashed assets, must not trigger a reload.
 */
export function isNewDeploy(current: string[], served: string[]): boolean {
  if (current.length === 0 || served.length === 0) return false;
  const known = new Set(current);
  return served.some((u) => !known.has(u));
}

async function defaultFetchServedHtml(): Promise<string | null> {
  try {
    const res = await fetch('/', { cache: 'no-store', headers: { accept: 'text/html' } });
    if (!res.ok) return null;
    return await res.text();
  } catch {
    return null;
  }
}

export interface DeployWatcherOptions {
  /** Fetches the live index.html; resolve null on any failure. */
  fetchServedHtml?: () => Promise<string | null>;
  /** Asset URLs of the running build (defaults to the document's). */
  currentUrls?: string[];
  /** Background poll cadence. */
  intervalMs?: number;
  /** Minimum spacing between checks (focus/visibility events are throttled to this). */
  minGapMs?: number;
  now?: () => number;
  /**
   * Measured server-minus-client clock offset in ms (see `getClockSkewMs`).
   * The build stamp is server time; this converts it to this machine's clock
   * so a skewed laptop can't make the button early or late.
   */
  clockSkewMs?: () => number;
}

export interface DeployWatcher {
  start(): void;
  stop(): void;
  /** Runs a check now (subject to the min-gap throttle); resolves the pending state. */
  check(): Promise<boolean>;
  isUpdatePending(): boolean;
  /**
   * Flag a newer build without polling. A failed lazy chunk is proof the build
   * moved, so the stale-chunk listener calls this instead of guessing.
   */
  markPending(): void;
  /** When the newer build was first flagged, or null while none is pending. */
  pendingSince(): number | null;
  /**
   * Whether a lazy chunk has actually failed to load on this tab. A polled
   * detection means "a newer build exists"; this means "something on this
   * screen is already broken", and the header pill skips its wait for it.
   */
  chunkFailed(): boolean;
  /** Called when a newer build is first detected, and again if a chunk then fails. */
  subscribe(fn: () => void): () => void;
}

export function createDeployWatcher(opts: DeployWatcherOptions = {}): DeployWatcher {
  const fetchServedHtml = opts.fetchServedHtml ?? defaultFetchServedHtml;
  const intervalMs = opts.intervalMs ?? DEFAULT_INTERVAL_MS;
  const minGapMs = opts.minGapMs ?? DEFAULT_MIN_GAP_MS;
  const now = opts.now ?? (() => Date.now());
  const clockSkewMs = opts.clockSkewMs ?? (() => 0);
  let current: string[] | null = opts.currentUrls ?? null;

  let pending = false;
  let pendingAt: number | null = null;
  let failed = false;
  let lastCheck = -Infinity;
  let inFlight: Promise<boolean> | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  const listeners = new Set<() => void>();

  // `since` is the served build's stamp in server time. The wait before the
  // header offers a button runs from the merge, so a tab that only noticed
  // ten minutes later is already ten minutes into it. Never in the future
  // (a stamp ahead of this clock reads as "just now"), and absent a stamp —
  // a 404'd chunk, a pre-stamp build — detection time is the best we have.
  const flagPending = (since?: number) => {
    if (pending) return;
    pending = true;
    const t = now();
    pendingAt = since === undefined ? t : Math.min(t, since - clockSkewMs());
    listeners.forEach((fn) => fn());
    stop();
  };

  const check = (): Promise<boolean> => {
    if (pending) return Promise.resolve(true);
    if (inFlight) return inFlight;
    if (now() - lastCheck < minGapMs) return Promise.resolve(false);
    lastCheck = now();
    if (current === null) current = typeof document === 'undefined' ? [] : currentAssetUrls();
    inFlight = fetchServedHtml()
      .then((html) => {
        if (html && isNewDeploy(current!, extractAssetUrls(html))) {
          flagPending(extractBuildTime(html) ?? undefined);
        }
        return pending;
      })
      .catch(() => pending)
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  };

  const onVisible = () => {
    if (document.visibilityState === 'visible') void check();
  };
  const onFocus = () => void check();

  const start = () => {
    if (timer || pending) return;
    timer = setInterval(() => void check(), intervalMs);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
  };

  const stop = () => {
    if (timer) clearInterval(timer);
    timer = null;
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    if (typeof window !== 'undefined') window.removeEventListener('focus', onFocus);
  };

  return {
    start,
    stop,
    check,
    isUpdatePending: () => pending,
    pendingSince: () => pendingAt,
    chunkFailed: () => failed,
    // A chunk can die after a poll already flagged the build, and the pill
    // must still learn the tab is broken, so subscribers hear it either way.
    markPending: () => {
      failed = true;
      if (pending) listeners.forEach((fn) => fn());
      else flagPending();
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
  };
}

// ---- App-wide singleton -------------------------------------------------

let watcher: DeployWatcher | null = null;

/** Wire the watcher once at boot (main.tsx). Re-installing replaces the previous one. */
export function installDeployWatcher(opts?: DeployWatcherOptions): DeployWatcher {
  watcher?.stop();
  watcher = createDeployWatcher(opts);
  watcher.start();
  return watcher;
}

export function isUpdatePending(): boolean {
  return watcher?.isUpdatePending() ?? false;
}

export function subscribeUpdatePending(fn: () => void): () => void {
  return watcher?.subscribe(fn) ?? (() => {});
}

/**
 * Flag a newer build from outside the poll loop. A 404'd lazy chunk is not a
 * guess about staleness, it is proof of it, so the stale-chunk listener routes
 * here and the header pill appears. No-ops before the watcher is installed.
 */
export function markUpdatePending(): void {
  watcher?.markPending();
}

/**
 * How long a newer build has been waiting, in ms, or null when none is. The
 * header pill uses this to stay quiet for a while: an advisor who navigates
 * inside that window rolls over silently and never sees a button at all.
 */
export function updatePendingForMs(nowMs = Date.now()): number | null {
  const since = watcher?.pendingSince() ?? null;
  return since === null ? null : Math.max(0, nowMs - since);
}

/**
 * Whether a lazy chunk has already failed on this tab. The pill's wait exists
 * to keep a button off the screen of someone whose tab still works; a tab
 * whose Import 1040 just died is not that tab, and the dialog's own text is
 * already pointing at the button, so the pill shows at once.
 */
export function updateBrokeThisTab(): boolean {
  return watcher?.chunkFailed() ?? false;
}

/**
 * Apply a pending update by reloading. Call ONLY from a safe point — a
 * navigation the advisor just made — never from a timer. Returns whether a
 * reload was started.
 */
export function rolloverIfUpdatePending(reload: () => void = () => window.location.reload()): boolean {
  if (!isUpdatePending()) return false;
  reload();
  return true;
}

export interface RolloverContext {
  /** The Index view being shown. */
  view: string;
  practiceMode: boolean;
  isDemoMode: boolean;
  isImpersonating: boolean;
}

/**
 * The standing-still destinations: a list or a settings page the advisor just
 * arrived at, with nothing in progress, where a reload puts them right back
 * where they were headed. Most of the app's sidebar lives at one URL, so
 * without these a whole session of clicking around would never pick up a new
 * build and the header pill would be carrying the whole job.
 *
 * Deliberately absent — a reload would interrupt real work or a client
 * presentation: 'wizard', 'results', 'compare', and 'client-view' (an open
 * client folder). 'assignments' is left out too, pending a call on it.
 *
 * Team Management and Super Admin need no entry: they are their own routes,
 * so `DeployRolloverOnNavigation` already catches them on the path change.
 */
const SAFE_ROLLOVER_VIEWS: ReadonlySet<string> = new Set([
  'dashboard',
  'org-scenarios',
  'drafts',
  'settings',
  'team',
]);

/**
 * True at a destination where applying a pending update is invisible. Anything
 * ephemeral that a reload would wipe — practice scenarios, demo data from
 * router state, a super-admin impersonation session — vetoes it.
 *
 * Role-agnostic on purpose: a role that treats one of these views as a
 * redirect stop rather than a destination guards at the call site.
 */
export function isSafeRolloverPoint(ctx: RolloverContext): boolean {
  return (
    SAFE_ROLLOVER_VIEWS.has(ctx.view) && !ctx.practiceMode && !ctx.isDemoMode && !ctx.isImpersonating
  );
}
